import React, { useMemo, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { COLOMBIA_AREAS, areaOf, canonicalCity, canonicalList, inCatalog, searchPlaces } from '../data/colombiaCities';

// Zonas de entrega: se escogen de la lista de ciudades y municipios de Colombia (nombre único, sin variantes) y quedan como etiquetas.
// Lo que no esté en la lista se puede agregar aparte; queda con mayúscula inicial y sin repetirse.
const SHORTCUTS = ['Bogotá', 'Medellín', 'Cali', 'Barranquilla', 'Cartagena', 'Bucaramanga', 'Cúcuta', 'Pereira', 'Manizales', 'Armenia', 'Ibagué', 'Villavicencio'];

export default function CityPicker({ value, onChange }) {
  const [query, setQuery] = useState('');
  const selected = useMemo(() => canonicalList(value), [value]);
  const results = searchPlaces(query);
  const typed = canonicalCity(query);
  const canAddTyped = typed.length >= 3 && !inCatalog(query) && !selected.includes(typed);

  const add = (names) => { onChange(canonicalList([...selected, ...names])); setQuery(''); };
  const remove = (name) => onChange(selected.filter((c) => c !== name));
  const mainSuggestions = SHORTCUTS.map((name) => COLOMBIA_AREAS.find((a) => a.city === name)).filter(Boolean);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5" aria-label="Ciudades elegidas">
        {selected.length === 0 && <span className="text-[11px] text-seryi-text">Aún no hay ciudades: el cliente no tendrá que elegir ciudad.</span>}
        {selected.map((c) => (
          <span key={c} className="inline-flex items-center gap-1 bg-seryi-green/10 text-seryi-green border border-seryi-green/30 rounded-full pl-2.5 pr-1 py-0.5 text-[11px] font-bold">
            {c}
            <button type="button" aria-label={`Quitar ${c}`} onClick={() => remove(c)} className="p-0.5 rounded-full hover:bg-seryi-green/20"><X size={11} /></button>
          </span>
        ))}
      </div>

      <input
        type="text" aria-label="Buscar ciudad o municipio" placeholder="Busca una ciudad o municipio (ej: Pereira, Dosquebradas)"
        className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-4 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-seryi-green"
        value={query} onChange={(e) => setQuery(e.target.value)}
      />

      {query.trim() && (
        <ul className="bg-seryi-bg-dark border border-seryi-bg-light rounded-xl divide-y divide-seryi-bg-light overflow-hidden" aria-label="Resultados">
          {results.map((p) => (
            <li key={`${p.name}-${p.department}`} className="flex items-center justify-between gap-2 px-3 py-2 text-xs text-seryi-heading">
              <span className="min-w-0 truncate">{p.name} <span className="text-seryi-text">· {p.department}{p.area ? ` · área de ${p.area}` : ''}</span></span>
              <span className="flex shrink-0 gap-1.5">
                <button type="button" disabled={selected.includes(p.name)} onClick={() => add([p.name])} className="inline-flex items-center gap-1 rounded-lg bg-seryi-green/10 text-seryi-green px-2 py-1 font-bold disabled:opacity-40"><Plus size={12} /> Agregar</button>
                {!p.area && areaOf(p.name).length > 1 && (
                  <button type="button" onClick={() => add(areaOf(p.name))} className="rounded-lg bg-seryi-bg-light/40 px-2 py-1 font-bold" aria-label={`Agregar ${p.name} y su área`}>+ su área</button>
                )}
              </span>
            </li>
          ))}
          {canAddTyped && (
            <li className="px-3 py-2 text-xs">
              <button type="button" onClick={() => add([typed])} className="font-bold text-seryi-heading underline">Agregar «{typed}» (no está en la lista)</button>
            </li>
          )}
          {results.length === 0 && !canAddTyped && <li className="px-3 py-2 text-xs text-seryi-text">Sin coincidencias.</li>}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[10px] text-seryi-text">Atajos:</span>
        {mainSuggestions.map((a) => (
          <button key={a.city} type="button" onClick={() => add(areaOf(a.city))} className="text-[10px] font-bold border border-seryi-bg-light rounded-full px-2 py-0.5 text-seryi-heading hover:bg-seryi-bg-light/40" aria-label={`Agregar ${a.city} y su área`}>
            {a.city} + área
          </button>
        ))}
      </div>
    </div>
  );
}
